import { useState, useEffect } from 'react';
import { AlertTriangle, Shield, Clock, X, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import api from '@/lib/api';

const BREAK_GLASS_REASONS = [
  { value: 'MEDICAL_EMERGENCY', label: 'Medical Emergency', description: 'General medical emergency requiring immediate access' },
  { value: 'PATIENT_UNCONSCIOUS', label: 'Patient Unconscious', description: 'Patient cannot provide consent for access' },
  { value: 'LIFE_THREATENING', label: 'Life Threatening', description: 'Life-threatening situation requiring urgent care' },
  { value: 'DISASTER_RECOVERY', label: 'Disaster Recovery', description: 'Mass casualty or disaster event' },
  { value: 'SYSTEM_FAILURE', label: 'System Failure', description: 'Normal access systems are unavailable' },
  { value: 'OTHER_EMERGENCY', label: 'Other Emergency', description: 'Other urgent situation' },
];

export default function BreakGlassButton({ user }) {
  const [showModal, setShowModal] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  // Form state
  const [reason, setReason] = useState('');
  const [justification, setJustification] = useState('');
  const [durationHours, setDurationHours] = useState('4');
  
  // Only show for admin and doctor roles
  const canUseBreakGlass = user?.role === 'admin' || user?.role === 'doctor';
  
  useEffect(() => {
    if (canUseBreakGlass) {
      checkBreakGlassStatus();
    }
  }, [canUseBreakGlass]);
  
  const checkBreakGlassStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await api.get('/security/break-glass/status');
      if (response.data.has_active_session) {
        setActiveSession(response.data.session);
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      console.error('Failed to check break-glass status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };
  
  const handleRequestAccess = async () => {
    // Validation
    if (!reason) {
      toast.error('Please select a reason for emergency access');
      return;
    }
    if (justification.length < 20) {
      toast.error('Justification must be at least 20 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/security/break-glass/request', {
        reason,
        justification,
        duration_hours: parseInt(durationHours)
      });
      
      if (response.data.success) {
        toast.success('Emergency access granted', {
          description: `Access valid for ${durationHours} hours. All actions will be logged.`
        });
        setActiveSession(response.data.session);
        setShowModal(false);
        // Reset form
        setReason('');
        setJustification('');
        setDurationHours('4');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request emergency access');
    } finally {
      setLoading(false);
    }
  };
  
  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };
  
  if (!canUseBreakGlass) return null;
  
  // Show active session indicator
  if (activeSession) {
    return (
      <>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setShowModal(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white animate-pulse"
          data-testid="break-glass-active-btn"
        >
          <Shield className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Emergency Access Active</span>
          <span className="sm:hidden">Active</span>
        </Button>
        
        {/* Active Session Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <Shield className="h-5 w-5" />
                Emergency Access Active
              </DialogTitle>
              <DialogDescription>
                You have elevated access to patient records.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  All your actions are being logged for HIPAA compliance review.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Reason:</span>
                  <Badge variant="outline" className="bg-amber-50">
                    {BREAK_GLASS_REASONS.find(r => r.value === activeSession.reason)?.label || activeSession.reason}
                  </Badge>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-slate-500">Justification:</span>
                  <span className="text-right max-w-[200px] text-slate-700">{activeSession.justification}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Time Remaining:</span>
                  <span className="font-medium text-amber-600 flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatTimeRemaining(activeSession.expires_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Started:</span>
                  <span className="text-slate-700">
                    {new Date(activeSession.created_at).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowModal(true)}
        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
        data-testid="break-glass-btn"
        disabled={checkingStatus}
      >
        {checkingStatus ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <AlertTriangle className="h-4 w-4 mr-2" />
        )}
        <span className="hidden sm:inline">Break Glass</span>
        <span className="sm:hidden">Emergency</span>
      </Button>
      
      {/* Request Access Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Emergency Access Request
            </DialogTitle>
            <DialogDescription>
              Break-Glass access provides temporary elevated access to patient records in emergency situations.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert className="border-red-200 bg-red-50">
              <Shield className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Warning:</strong> This action is heavily audited. All access will be logged and reviewed by administrators. Only use in genuine emergencies.
              </AlertDescription>
            </Alert>
            
            {/* Reason Select */}
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-sm font-medium">
                Emergency Reason <span className="text-red-500">*</span>
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="reason" data-testid="break-glass-reason">
                  <SelectValue placeholder="Select the type of emergency" />
                </SelectTrigger>
                <SelectContent>
                  {BREAK_GLASS_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{r.label}</span>
                        <span className="text-xs text-slate-500">{r.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Justification */}
            <div className="space-y-2">
              <Label htmlFor="justification" className="text-sm font-medium">
                Justification <span className="text-red-500">*</span>
                <span className="text-xs text-slate-500 ml-2">(minimum 20 characters)</span>
              </Label>
              <Textarea
                id="justification"
                data-testid="break-glass-justification"
                placeholder="Describe the emergency situation and why immediate access is required..."
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                className={justification.length > 0 && justification.length < 20 ? 'border-red-300' : ''}
              />
              <div className="flex justify-between text-xs">
                <span className={justification.length < 20 ? 'text-red-500' : 'text-green-600'}>
                  {justification.length}/20 minimum characters
                </span>
                {justification.length >= 20 && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
              </div>
            </div>
            
            {/* Duration */}
            <div className="space-y-2">
              <Label htmlFor="duration" className="text-sm font-medium">
                Access Duration
              </Label>
              <Select value={durationHours} onValueChange={setDurationHours}>
                <SelectTrigger id="duration" data-testid="break-glass-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="4">4 hours (recommended)</SelectItem>
                  <SelectItem value="6">6 hours</SelectItem>
                  <SelectItem value="8">8 hours (maximum)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Access automatically expires after the selected duration.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowModal(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRequestAccess}
              disabled={loading || !reason || justification.length < 20}
              className="bg-red-600 hover:bg-red-700"
              data-testid="break-glass-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Activate Emergency Access
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
